// © Microsoft Corporation. All rights reserved.

import { PageRequestTargetScope } from "@msnews/config-data-types";

/**
 * TargetScopeMatcher
 * This is for comparing targetscope from the config with targetscope from the page.
 */

/**
 * basic version of target scope that only checks market currently.
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope matches with config target scope, otherwise false
 */
export function targetScopeMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    // if target scope is undefined for a config entry, we treat that as default config and apply it
    if (!configTargetScope) {
        return true;
    }

    return (audienceModeMatched(configTargetScope, pageTargetScope)
        && browserTypeMatched(configTargetScope, pageTargetScope)
        && deviceTypeMatched(configTargetScope, pageTargetScope)
        && domainMatched(configTargetScope, pageTargetScope)
        && experimentMatched(configTargetScope, pageTargetScope)
        && marketMatched(configTargetScope, pageTargetScope)
        && osMatched(configTargetScope, pageTargetScope)
        && pageTypeMatched(configTargetScope, pageTargetScope)
        && platformMatched(configTargetScope, pageTargetScope));
}

/**
 * function to check if current audienceMode type matches target audienceMode type
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope audienceMode matches with config target scope audienceMode, otherwise false
 */
function audienceModeMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return !configTargetScope.audienceMode || pageTargetScope.audienceMode === configTargetScope.audienceMode;
}

/**
 * function to check if current browser type matches target browser type
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope browser type matches with config target scope browser type, otherwise false
 */
function browserTypeMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    if (!configTargetScope.browser || !configTargetScope.browser.browserType) {
        return true;
    }

    if (pageTargetScope.browser && pageTargetScope.browser.browserType === configTargetScope.browser.browserType &&
        (!configTargetScope.browser.version || pageTargetScope.browser.version === configTargetScope.browser.version)) {
        return true;
    }

    return false;
}

/**
 * function to check if current device type matches target device type
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope device type matches with config target scope device type, otherwise false
 */
function deviceTypeMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return !configTargetScope.deviceFormFactor || pageTargetScope.deviceFormFactor === configTargetScope.deviceFormFactor;
}

/**
 * Check if current domain matches target domain
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return Whether the current target scope domain matches with config target scope domain
 */
function domainMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return !configTargetScope.domain || pageTargetScope.domain === configTargetScope.domain;
}

/**
 * function to check if current experiment id matches with the requested scope experiment id.
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope experiment matches with config target scope experiment, otherwise false
 */
function experimentMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    pageTargetScope.pageExperiments = pageTargetScope.pageExperiments || [];

    if (typeof configTargetScope.experimentId != "string" || Object.keys(configTargetScope.experimentId).length === 0
        || pageTargetScope.pageExperiments.indexOf(configTargetScope.experimentId) >= 0) {
        return true;
    }

    return false;
}

/**
 * Compare the strings case insensitive.
 * @private
 * @param {string} lhs The string to compare.
 * @param {string} rhs The string to compare against.
 * @return {number} - true if the strings equal case insensitive.
 */
function stringsEqualCaseInsensitive(lhs: string, rhs: string): boolean {
    return (lhs || "").localeCompare(rhs || "", undefined, { sensitivity: "accent" }) === 0;
}

/**
 * function to check if current market scope match with target scope market.
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope market matches with config target scope market, otherwise false
 */
function marketMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    // if locale is not part of target scope, we assume it is applicable for all locale.
    // Separated out this if condition to have clarity about this behavior.
    if (!configTargetScope.locale) {
        return true;
    }

    const targetLocale = configTargetScope.locale;
    const currentLocale = pageTargetScope.locale;

    if ((!targetLocale.language || stringsEqualCaseInsensitive(targetLocale.language, currentLocale.language)) &&
        (!targetLocale.market || stringsEqualCaseInsensitive(targetLocale.market, currentLocale.market)) &&
        (!targetLocale.script || stringsEqualCaseInsensitive(targetLocale.script, currentLocale.script))) {
        return true;
    }

    return false;
}

/**
 * function to check if current operating system type matches target operating system type
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope operating system type matches with config target scope operating system type, otherwise false
 */
function osMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return !configTargetScope.os || pageTargetScope.os === configTargetScope.os;
}

/**
 * function to check if current pagetype matches target scope
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope pagetype matches with config target scope pagetype, otherwise false
 */
function pageTypeMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return !configTargetScope.pageType || pageTargetScope.pageType === configTargetScope.pageType;
}

/**
 * function to check if current scope platform type matches target scope platform type
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope platform matches with config target scope platform, otherwise false
 */
function platformMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return !configTargetScope.platform || pageTargetScope.platform === configTargetScope.platform;
}
